import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { Loader } from 'semantic-ui-react';

import classNames from 'classnames';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import '../../css/BasicIframeModal.less';

/**
 * BasicIframeModal - a lightweight modal component capable of displaying a provided URL within an iframe. Resizable and draggable.
 *
 * @description
 * This modal is intended to be launched via BasicIframeModalLauncherButton or BasicIframeModalLauncherIcon
 * rather than being used directly. The launchers provide a more convenient setup to handle the modal lifecycle automatically.
 *
 * @see BasicIframeModalLauncherButton - For button-triggered modals
 * @see BasicIframeModalLauncherIcon - For floating icon-triggered modals
 */
const BasicIframeModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'close',
    buttonKeyConfirmLabel = '',
    className = 'BasicIframeModal',
    classNameForGenericModalParent = 'generic-modal-parent-BasicIframeModal',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disableKeystroke = false,
    disableMove = false,
    disableResize = false,
    initHeight = props.initHeight || Math.floor(window.innerHeight * 0.85),
    initWidth = props.initWidth || Math.floor(window.innerWidth * 0.85),
    minHeight,
    minWidth,
    onOpen = () => {},
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    postMessageParamObj,
    postMessageTargetOrigin = '*',
    top = 75,
    url = '',
    onClose,
    iframeProps = props?.iframeProps || {
      allow: 'fullscreen;autoplay;camera;microphone',
      scrolling: 'no' // help prevent double scrollbars inside of iframe
    },
    t
  } = props;

  const headerTitle = props.headerTitle || t('externalContent');

  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);
  const hasPostMessageBeenSent = useRef(false);

  const GenericModal = SatCoreComponent('GenericModal');

  /**
   * Send custom parameters to the iframe via postMessage API
   *
   * Sends configured parameters (e.g., authKey, userId) to the iframe's content window after it loads.
   * This is typically used when third-party content providers (e.g. StringServe, Carolina, etc.)
   * control the iframe content and need authentication or user context data.
   *
   * Parameters are sent automatically on iframe load.
   *
   * @example
   * // Third-party content provider (e.g. StringServe, Carolina, etc.) should add this to their iframe content:
   * window.addEventListener('message', (event) => {
   *   console.log('Received message event from parent:', event);
   *   // event.data = { authKey: '...', userId: '...' }
   *
   *   const { authKey, userId } = event.data;
   *   if (authKey && userId) {
   *    // Third-party can use these parameters as-needed (e.g. authentication, user context, etc.)
   *   }
   * });
   * // Optional: Manually request params in case of timing issues preventing message receipt on load.
   * // This helps ensure receival of the needed event data.
   * window.parent.postMessage({ isParamsRequest: true }, '*');
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
   */
  const sendPostMessageParamsToIframe = useCallback(() => {
    // Only send if there are postMessage params configured
    if (!postMessageParamObj || Object.keys(postMessageParamObj)?.length === 0) {
      return;
    }

    // Prevent duplicate sends
    if (hasPostMessageBeenSent.current) {
      return;
    }

    if (!iframeRef.current?.contentWindow) {
      console.warn('[BasicIframeModal] iframe contentWindow not available');
      return;
    }

    try {
      console.info('[BasicIframeModal] Sending postMessage params to iframe...');
      iframeRef.current.contentWindow.postMessage(
        postMessageParamObj,
        postMessageTargetOrigin
      );

      hasPostMessageBeenSent.current = true;
      console.info('[BasicIframeModal] postMessage sent successfully');
    } catch (error) {
      console.error('[BasicIframeModal] Failed to send postMessage params to iframe:', error);
    }
  }, [postMessageParamObj, postMessageTargetOrigin]);

  // Listen for param requests from iframe
  useEffect(() => {
    const handleIframeRequest = (event) => {
      // Check if iframe is requesting params
      if (event?.data?.isParamsRequest === true) {
        console.info('[BasicIframeModal] Iframe requested params, sending...');
        hasPostMessageBeenSent.current = false; // Reset to allow resend
        sendPostMessageParamsToIframe();
      }
    };

    window.addEventListener('message', handleIframeRequest);

    return () => {
      window.removeEventListener('message', handleIframeRequest);
    };
  }, [sendPostMessageParamsToIframe]);

  const handleClose = async (event, actionButtonData = {}) => {
    setIsLoading(true);

    if (typeof onClose === 'function') {
      await onClose(event, actionButtonData);
    }

    setIsLoading(false);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    setTimeout(() => {
      sendPostMessageParamsToIframe();
    }, 1000);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    console.error('Failed to load iframe content');
  };

  const renderModalBody = () => {
    if (!url) {
      return (
        <div className='basic-iframe-modal-error'>
          <p>{t('noUrlProvided')}</p>
        </div>
      );
    }

    return (
      <div className='basic-iframe-modal-content'>
        {isLoading && (
          <div className='basic-iframe-modal-loader'>
            <Loader active />
          </div>
        )}
        <iframe
          ref={iframeRef}
          height='100%'
          onError={handleIframeError}
          onLoad={handleIframeLoad}
          src={url}
          style={{
            border: 'none',
            display: isLoading ? 'none' : 'block',
            overflow: 'hidden' // help prevent double scrollbars inside of iframe
          }}
          title={headerTitle}
          width='100%'
          {...iframeProps}
        />
      </div>
    );
  };

  return (
    <GenericModal
      bodyMsg={renderModalBody}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={buttonKeyConfirmLabel}
      className={classNames(className, {
        'basic-iframe-modal-loading': isLoading,
        'basic-iframe-modal-no-url': !url
      })}
      classNameForGenericModalParent={classNameForGenericModalParent}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableConfirmButton={!buttonKeyConfirmLabel}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={headerTitle}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isConfirmButtonLoading={false}
      isLoading={false}
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top}
    />
  );
});

export default BasicIframeModal;

SatCoreRegister('BasicIframeModal', BasicIframeModal);
